import type { SkillSummary } from '@/hooks/use-profile'
import { matchesProfileSkillName } from '@/lib/profile-evidence-display'

/** Backend proficiency labels in display order (highest first). */
export const PROFICIENCY_ORDER = ['Expert', 'Advanced', 'Working', 'Foundational'] as const

export type ProficiencyLabel = (typeof PROFICIENCY_ORDER)[number]

export type DemonstratedSkillProficiencyGroup = {
  label: ProficiencyLabel
  items: SkillSummary[]
}

export type UnifiedSkillItem =
  | { type: 'demonstrated'; summary: SkillSummary }
  | { type: 'claimed'; skillName: string }

/** Cap for the public `/p/:slug` skills card — highest-scored demonstrated skills only. */
export const PUBLIC_PROFILE_TOP_SKILLS_LIMIT = 10

/** Demonstrated skills sorted by score, then name. */
export function sortDemonstratedSkillSummaries(summaries: SkillSummary[]): SkillSummary[] {
  return [...summaries].sort((a, b) => {
    if (b.proficiency_score !== a.proficiency_score) {
      return b.proficiency_score - a.proficiency_score
    }
    return a.skill_name.trim().localeCompare(b.skill_name.trim(), undefined, { sensitivity: 'base' })
  })
}

/** Highest-scored demonstrated skills, already sorted by `sortDemonstratedSkillSummaries`. */
export function takeTopDemonstratedSkills(
  summaries: SkillSummary[],
  limit = PUBLIC_PROFILE_TOP_SKILLS_LIMIT,
): SkillSummary[] {
  return summaries.slice(0, limit)
}

/** Profile skills without linked evidence (not present in skill_summaries). */
export function splitClaimedOnlySkills(
  allSkills: string[] | undefined,
  demonstratedSummaries: SkillSummary[] | undefined
): string[] {
  const demonstrated = new Set(
    (demonstratedSummaries ?? []).map((s) => s.skill_name.trim().toLowerCase())
  )
  return (allSkills ?? [])
    .filter((skill) => !demonstrated.has(skill.trim().toLowerCase()))
    .sort((a, b) => a.trim().localeCompare(b.trim(), undefined, { sensitivity: 'base' }))
}

/** Group demonstrated skills by proficiency tier; empty tiers omitted. Order preserved within each tier. */
export function groupDemonstratedSkillsByProficiency(
  summaries: SkillSummary[]
): DemonstratedSkillProficiencyGroup[] {
  return PROFICIENCY_ORDER.map((label) => ({
    label,
    items: summaries.filter((summary) => summary.proficiency_label === label),
  })).filter((group) => group.items.length > 0)
}

/** Single skills list: demonstrated first (score desc), then claimed-only (A–Z). */
export function buildUnifiedSkillItems(
  allSkills: string[] | undefined,
  skillSummaries: SkillSummary[] | undefined
): UnifiedSkillItem[] {
  const demonstrated = sortDemonstratedSkillSummaries(skillSummaries ?? [])
  const claimedOnly = splitClaimedOnlySkills(allSkills, skillSummaries)
  return [
    ...demonstrated.map((summary) => ({ type: 'demonstrated' as const, summary })),
    ...claimedOnly.map((skillName) => ({ type: 'claimed' as const, skillName })),
  ]
}

export function skillItemDisplayName(item: UnifiedSkillItem): string {
  return item.type === 'demonstrated' ? item.summary.skill_name : item.skillName
}

export function unifiedSkillItemKey(item: UnifiedSkillItem): string {
  return item.type === 'demonstrated' ? item.summary.skill_id : item.skillName.trim().toLowerCase()
}

/**
 * During an open skills-manager session, pin newly added skills directly under the add row
 * (most recent first). Unpinned items keep their default sort order.
 */
export function orderSkillItemsWithSessionPins(
  items: UnifiedSkillItem[],
  pinnedNames: string[]
): UnifiedSkillItem[] {
  if (pinnedNames.length === 0) return items

  const placedKeys = new Set<string>()
  const pinnedItems: UnifiedSkillItem[] = []

  for (const pinnedName of pinnedNames) {
    const match = items.find(
      (item) =>
        !placedKeys.has(unifiedSkillItemKey(item)) &&
        matchesProfileSkillName(pinnedName, skillItemDisplayName(item))
    )
    if (match) {
      pinnedItems.push(match)
      placedKeys.add(unifiedSkillItemKey(match))
    }
  }

  const rest = items.filter((item) => !placedKeys.has(unifiedSkillItemKey(item)))
  return [...pinnedItems, ...rest]
}
